import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import {
  getSchoolGallery,
  addPhotoToSchoolGallery,
  getKeyToAddPhoto,
  deletePhotoFromSchoolGallery,
  changeSchoolPhotoInfo,
  reorderSchoolGalleryPhotos,
  getSchoolAlbums,
  createSchoolAlbum,
  updateSchoolAlbum,
  deleteSchoolAlbum
} from 'Src/helpers/service/admin/schoolGallery';
import { Photo } from 'Src/models/photo';
import { SchoolAlbum, SCHOOL_ALBUM_CATEGORY } from 'Src/models/album';
import { ACCESS_PRESET } from 'Src/consts/album';
import ReactCrop, { PixelCrop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './SchoolGallery.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface UploadItem {
  id: string;
  file: File;
  previewUrl: string;
  caption: string;
  isCropped: boolean;
}

type GallerySegment = 'all' | 'published' | 'hidden';

interface State {
  albums: SchoolAlbum[];
  selectedAlbumId: string;
  isAlbumEditorOpen: boolean;
  editingAlbumId: string;
  albumDraft: {
    name: string;
    description: string;
    academicYear: string;
    category: SCHOOL_ALBUM_CATEGORY;
    accessPreset: 'PUBLIC' | 'PRIVATE';
    includeOnBigscreen: boolean;
  };
  photos: Photo[];
  photosCount: number;
  fullScreenPhotoId: string;
  isLoading: boolean;
  isUploading: boolean;
  isUploadOpen: boolean;
  uploadQueue: UploadItem[];
  dragPhotoId: string;
  overPhotoId: string;
  isReordering: boolean;
  captionEditPhotoId: string;
  captionValue: string;
  captionDescription: string;
  captionAccessPreset: Photo['accessPreset'];
  toast: string;
  cropUploadItemId: string;
  crop: PercentCrop;
  completedCrop: PixelCrop | null;
  deletePhotoId: string;
  search: string;
  searchDraft: string;
  segment: GallerySegment;
  loadError: string;
}

export class SchoolGallery extends Component<Props, State> {
  toastTimer: any;

  constructor(props) {
    super(props);

    this.state = {
      albums: [],
      selectedAlbumId: '',
      isAlbumEditorOpen: false,
      editingAlbumId: '',
      albumDraft: {
        name: '',
        description: '',
        academicYear: this.getCurrentAcademicYear(),
        category: 'GENERAL',
        accessPreset: 'PUBLIC',
        includeOnBigscreen: false
      },
      photos: [],
      photosCount: 0,
      fullScreenPhotoId: '',
      isLoading: false,
      isUploading: false,
      isUploadOpen: false,
      uploadQueue: [],
      dragPhotoId: '',
      overPhotoId: '',
      isReordering: false,
      captionEditPhotoId: '',
      captionValue: '',
      captionDescription: '',
      captionAccessPreset: ACCESS_PRESET.PRIVATE as Photo['accessPreset'],
      toast: '',
      cropUploadItemId: '',
      crop: { unit: '%', x: 5, y: 35, width: 90, height: 30, aspect: 3 },
      completedCrop: null,
      deletePhotoId: '',
      search: '',
      searchDraft: '',
      segment: 'all',
      loadError: ''
    };
  }

  cropImage: HTMLImageElement | null = null;

  componentDidMount() {
    this.loadAlbums();
  }

  componentWillUnmount() {
    clearTimeout(this.toastTimer);
    this.state.uploadQueue.forEach(item => URL.revokeObjectURL(item.previewUrl));
  }

  getAlbumId() {
    return this.state.selectedAlbumId || propz.get(this.props.user, ['activeSchool', 'defaultAlbumId'], '');
  }

  getDefaultAlbumId() {
    return propz.get(this.props.user, ['activeSchool', 'defaultAlbumId'], '');
  }

  isHomepageGallery(albumId = this.getAlbumId()) {
    return Boolean(albumId) && albumId === this.getDefaultAlbumId();
  }

  getAlbumAccessPreset() {
    const album = this.state.albums.find(item => item.id === this.getAlbumId());
    return (album && album.accessPreset === ACCESS_PRESET.PRIVATE
      ? ACCESS_PRESET.PRIVATE
      : album && album.accessPreset === ACCESS_PRESET.PUBLIC
      ? ACCESS_PRESET.PUBLIC
      : propz.get(this.props.user, ['activeSchool', 'photoAccessPreset'], ACCESS_PRESET.PUBLIC)) as Photo['accessPreset'];
  }

  getCurrentAcademicYear() {
    const now = new Date();
    const start = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    return `${start}/${String(start + 1).slice(-2)}`;
  }

  loadAlbums = () => {
    this.setState({ isLoading: true, loadError: '' });
    getSchoolAlbums(this.props.user)
      .then(albums => {
        const defaultAlbumId = this.getDefaultAlbumId();
        const selectedAlbumId =
          this.state.selectedAlbumId ||
          (albums.some(album => album.id === defaultAlbumId) ? defaultAlbumId : propz.get(albums, [0, 'id'], ''));
        this.setState({ albums, selectedAlbumId }, this.load);
      })
      .catch(() =>
        this.setState({
          isLoading: false,
          loadError: 'Albums could not be loaded. Check your connection and try again.'
        })
      );
  };

  selectAlbum = (selectedAlbumId: string) => {
    this.setState({ selectedAlbumId, photos: [], fullScreenPhotoId: '', isReordering: false }, this.load);
  };

  openAlbumEditor = (album?: SchoolAlbum) => {
    this.setState({
      isAlbumEditorOpen: true,
      editingAlbumId: album ? album.id : '',
      albumDraft: {
        name: album ? album.name : '',
        description: album && album.description ? album.description : '',
        academicYear: album && album.academicYear ? album.academicYear : this.getCurrentAcademicYear(),
        category: (album && album.category ? album.category : 'GENERAL') as SCHOOL_ALBUM_CATEGORY,
        accessPreset: album && album.accessPreset === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC',
        includeOnBigscreen: Boolean(album && album.includeOnBigscreen)
      }
    });
  };

  saveAlbum = () => {
    const draft = this.state.albumDraft;
    if (!draft.name.trim()) return;
    const isEditing = Boolean(this.state.editingAlbumId);
    this.setState({ isLoading: true });
    const data = { ...draft, name: draft.name.trim(), description: draft.description.trim(), includeOnBigscreen: draft.accessPreset === 'PUBLIC' && !this.isHomepageGallery(this.state.editingAlbumId) ? draft.includeOnBigscreen : false };
    const request = this.state.editingAlbumId
      ? updateSchoolAlbum(this.props.user, this.state.editingAlbumId, data)
      : createSchoolAlbum(this.props.user, data);
    request
      .then(album => {
        this.setState({ isAlbumEditorOpen: false, editingAlbumId: '', selectedAlbumId: album.id });
        this.showToast(isEditing ? 'Album updated' : 'Album created');
        this.loadAlbums();
      })
      .catch(() => {
        this.setState({ isLoading: false });
        this.showToast('Album could not be saved. Please try again.');
      });
  };

  removeCurrentAlbum = () => {
    const albumId = this.getAlbumId();
    const defaultAlbumId = propz.get(this.props.user, ['activeSchool', 'defaultAlbumId'], '');
    if (!albumId || albumId === defaultAlbumId) return;
    if (!window.confirm('Delete this album and all photos in it?')) return;
    this.setState({ isLoading: true });
    deleteSchoolAlbum(this.props.user, albumId)
      .then(() => this.setState({ selectedAlbumId: '' }, this.loadAlbums))
      .catch(() => {
        this.setState({ isLoading: false });
        this.showToast('Album could not be deleted.');
      });
  };

  getGalleryTitle() {
    return String(propz.get(this.props.user, ['activeSchool', 'kind'], '')) === 'SchoolUnion'
      ? 'Union gallery'
      : 'School gallery';
  }

  getOwnerLabel() {
    return String(propz.get(this.props.user, ['activeSchool', 'kind'], '')) === 'SchoolUnion' ? 'union' : 'school';
  }

  load = () => {
    this.setState({ isLoading: true, loadError: '' });

    if (!this.getAlbumId()) {
      this.setState({ photos: [], photosCount: 0, isLoading: false });
      return;
    }

    getSchoolGallery(this.props.user, this.getAlbumId())
      .then(photos => {
        this.setState({
          photos,
          photosCount: photos.length,
          isLoading: false,
          loadError: ''
        });
      })
      .catch(error => {
        const status = error && error.response && error.response.status;
        this.setState({
          isLoading: false,
          loadError: status
            ? `${this.getGalleryTitle()} could not be loaded (server error ${status}).`
            : `${this.getGalleryTitle()} could not be loaded. Check your connection and try again.`
        });
      });
  };

  icon(path: string, className?: string) {
    return (
      <svg
        className={className || 'schoolGalleryIcon'}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={path} />
      </svg>
    );
  }

  showToast = (toast: string) => {
    clearTimeout(this.toastTimer);
    this.setState({ toast });
    this.toastTimer = setTimeout(() => this.setState({ toast: '' }), 2400);
  };

  getCaption(photo: Photo) {
    return photo.name || photo.description || 'Untitled photo';
  }

  isPhotoVisible(photo: Photo) {
    return photo.accessPreset === ACCESS_PRESET.PUBLIC;
  }

  getVisiblePhotos() {
    const search = this.state.search.trim().toLowerCase();

    return this.state.photos.filter(photo => {
      const isVisible = this.isPhotoVisible(photo);
      if (this.state.segment === 'published' && !isVisible) return false;
      if (this.state.segment === 'hidden' && isVisible) return false;
      if (
        search &&
        !this.getCaption(photo)
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }
      return true;
    });
  }

  isFilteredView() {
    return this.state.segment !== 'all' || Boolean(this.state.search.trim());
  }

  setSegment = (segment: GallerySegment) => {
    this.setState({ segment, dragPhotoId: '', overPhotoId: '', isReordering: false });
  };

  setSearch = (search: string) => {
    this.setState({ search, searchDraft: search, dragPhotoId: '', overPhotoId: '', isReordering: false });
  };

  applySearch = () => {
    this.setState(state => ({ search: state.searchDraft.trim(), dragPhotoId: '', overPhotoId: '', isReordering: false }));
  };

  clearCriteria = () => {
    this.setState({ search: '', searchDraft: '', segment: 'all', dragPhotoId: '', overPhotoId: '', isReordering: false });
  };

  onFilesSelected = (files: FileList | File[]) => {
    const queue = Array.from(files)
      .filter(file => file.type.indexOf('image/') === 0)
      .map(file => ({
        id: `${Date.now()}_${Math.random()
          .toString(36)
          .slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        caption: file.name.replace(/\.[^/.]+$/, ''),
        isCropped: false
      }));

    if (queue.length === 0) return;

    this.setState(state => ({
      isUploadOpen: true,
      uploadQueue: [...state.uploadQueue, ...queue],
      cropUploadItemId: state.cropUploadItemId || (this.isHomepageGallery() ? queue[0].id : ''),
      completedCrop: null
    }));
  };

  openCrop = (id: string) => {
    this.cropImage = null;
    this.setState({
      cropUploadItemId: id,
      crop: this.isHomepageGallery()
        ? { unit: '%', x: 5, y: 35, width: 90, height: 30, aspect: 3 }
        : { unit: '%', x: 5, y: 5, width: 90, height: 90 },
      completedCrop: null
    });
  };

  onCropImageLoad = (image: HTMLImageElement) => {
    this.cropImage = image;
    const imageAspect = image.width / image.height;
    const targetAspect = this.isHomepageGallery() ? 3 : undefined;
    const width = targetAspect ? (imageAspect > targetAspect ? (90 * targetAspect) / imageAspect : 90) : 90;
    const height = targetAspect ? (imageAspect > targetAspect ? 90 : (90 * imageAspect) / targetAspect) : 90;
    const crop: PercentCrop = {
      unit: '%',
      x: (100 - width) / 2,
      y: (100 - height) / 2,
      width,
      height,
      ...(targetAspect ? { aspect: targetAspect } : {})
    };
    this.setState({
      crop,
      completedCrop: {
        unit: 'px',
        x: (crop.x / 100) * image.width,
        y: (crop.y / 100) * image.height,
        width: (crop.width / 100) * image.width,
        height: (crop.height / 100) * image.height,
        ...(targetAspect ? { aspect: targetAspect } : {})
      }
    });
    return false;
  };

  applyCrop = () => {
    const { cropUploadItemId, completedCrop, uploadQueue } = this.state;
    const image = this.cropImage;
    const item = uploadQueue.find(queueItem => queueItem.id === cropUploadItemId);
    if (!image || !item || !completedCrop || !completedCrop.width || !completedCrop.height) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const sourceWidth = completedCrop.width * scaleX;
    const sourceHeight = completedCrop.height * scaleY;
    const outputWidth = Math.max(1, Math.min(this.isHomepageGallery() ? 1800 : 2400, Math.round(sourceWidth)));
    const outputHeight = Math.max(
      1,
      Math.round(this.isHomepageGallery() ? outputWidth / 3 : outputWidth * (sourceHeight / sourceWidth))
    );
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    const outputType = item.file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    canvas.toBlob(
      blob => {
        if (!blob) return;
        const croppedFile = new File([blob], item.file.name, { type: outputType, lastModified: Date.now() });
        const previewUrl = URL.createObjectURL(croppedFile);
        URL.revokeObjectURL(item.previewUrl);
        this.setState(state => {
          const uploadQueue = state.uploadQueue.map(queueItem =>
            queueItem.id === item.id ? { ...queueItem, file: croppedFile, previewUrl, isCropped: true } : queueItem
          );
          const nextItem = uploadQueue.find(queueItem => !queueItem.isCropped);
          this.cropImage = null;
          return {
            uploadQueue,
            cropUploadItemId: nextItem ? nextItem.id : '',
            completedCrop: null
          };
        });
      },
      outputType,
      0.92
    );
  };

  removeUploadItem = (id: string) => {
    const item = this.state.uploadQueue.find(queueItem => queueItem.id === id);
    if (item) URL.revokeObjectURL(item.previewUrl);
    this.setState(state => ({ uploadQueue: state.uploadQueue.filter(queueItem => queueItem.id !== id) }));
  };

  setUploadCaption = (id: string, caption: string) => {
    this.setState(state => ({
      uploadQueue: state.uploadQueue.map(item => (item.id === id ? { ...item, caption } : item))
    }));
  };

  closeUpload = () => {
    this.state.uploadQueue.forEach(item => URL.revokeObjectURL(item.previewUrl));
    this.setState({ isUploadOpen: false, uploadQueue: [] });
  };

  onSubmitUpload = () => {
    const { user } = this.props;
    const albumId = this.getAlbumId();
    const photoAccessPreset = this.getAlbumAccessPreset();
    const queue = this.state.uploadQueue;

    if (queue.length === 0) return;

    this.setState({ isUploading: true });

    const promises = queue.map(async item => {
      const uploadFile = await this.optimiseImageFile(item.file);
      const formData = new FormData();
      formData.append('image', uploadFile, uploadFile.name);

      return getKeyToAddPhoto(user, formData).then(keyObj => {
        const data = {
          name: item.caption.trim(),
          description: '',
          picUrl: `${window.apiImg}/images/${keyObj.key}`,
          accessPreset: photoAccessPreset
        };

        return addPhotoToSchoolGallery(user, albumId, data);
      });
    });

    Promise.all(promises)
      .then(() => {
        this.closeUpload();
        this.setState({ isUploading: false });
        this.showToast(`${queue.length} photo${queue.length === 1 ? '' : 's'} added to the gallery`);
        this.load();
      })
      .catch(() => {
        this.setState({ isUploading: false });
        this.showToast('Photos could not be uploaded. Please try again.');
      });
  };

  optimiseImageFile = (file: File): Promise<File> => {
    const maxDimension = this.isHomepageGallery() ? 1800 : 2400;
    return new Promise(resolve => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
        if (scale === 1 && file.size <= 4 * 1024 * 1024) return resolve(file);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) return resolve(file);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob(
          blob => resolve(blob ? new File([blob], file.name, { type: outputType, lastModified: Date.now() }) : file),
          outputType,
          0.88
        );
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      image.src = url;
    });
  };

  onPhotoClick = (id: string): void => {
    this.setState({ fullScreenPhotoId: id });
  };

  closeLightbox = () => this.setState({ fullScreenPhotoId: '' });

  getFullScreenPhotoIndex() {
    return this.state.photos.findIndex(photo => photo.id === this.state.fullScreenPhotoId);
  }

  onPrevPhotoClick = event => {
    event.stopPropagation();
    const { photos } = this.state;
    const index = this.getFullScreenPhotoIndex();
    this.setState({ fullScreenPhotoId: index === 0 ? photos[photos.length - 1].id : photos[index - 1].id });
  };

  onNextPhotoClick = event => {
    event.stopPropagation();
    const { photos } = this.state;
    const index = this.getFullScreenPhotoIndex();
    this.setState({ fullScreenPhotoId: index === photos.length - 1 ? photos[0].id : photos[index + 1].id });
  };

  onDeletePhotoClick = (photoId: string) => {
    this.setState({ deletePhotoId: photoId });
  };

  confirmDeletePhoto = () => {
    const photoId = this.state.deletePhotoId;
    if (!photoId) return;
    this.setState({ isLoading: true });

    deletePhotoFromSchoolGallery(this.props.user, this.getAlbumId(), photoId)
      .then(() => {
        this.setState({ fullScreenPhotoId: '', deletePhotoId: '' });
        this.showToast('Photo deleted');
        this.load();
      })
      .catch(() => {
        this.setState({ isLoading: false });
        this.showToast('Photo could not be deleted. Please try again.');
      });
  };

  movePhotoBy = (photoId: string, offset: number) => {
    const index = this.state.photos.findIndex(photo => photo.id === photoId);
    const target = this.state.photos[index + offset];
    if (index < 0 || !target) return;
    this.movePhoto(photoId, target.id);
  };

  toggleVisibility = (photo: Photo) => {
    const nextPreset = (this.isPhotoVisible(photo)
      ? ACCESS_PRESET.PRIVATE
      : ACCESS_PRESET.PUBLIC) as Photo['accessPreset'];
    const data = { accessPreset: nextPreset };

    this.setState(state => ({
      photos: state.photos.map(item => (item.id === photo.id ? { ...item, accessPreset: nextPreset } : item))
    }));

    changeSchoolPhotoInfo(this.props.user, this.getAlbumId(), photo.id, data)
      .then(updatedPhoto => {
        this.setState(state => ({
          photos: state.photos.map(item => (item.id === photo.id ? updatedPhoto : item))
        }));
        this.showToast(nextPreset === ACCESS_PRESET.PUBLIC ? 'Photo published' : 'Photo hidden from public gallery');
      })
      .catch(() => {
        this.setState(state => ({
          photos: state.photos.map(item => (item.id === photo.id ? photo : item))
        }));
        this.showToast('Photo visibility could not be updated. Please try again.');
      });
  };

  openCaptionEdit = (photo: Photo) => {
    this.setState({
      captionEditPhotoId: photo.id,
      captionValue: photo.name || '',
      captionDescription: photo.description || '',
      captionAccessPreset: photo.accessPreset
    });
  };

  setAlbumCover = (photo: Photo) => {
    updateSchoolAlbum(this.props.user, this.getAlbumId(), { coverPhotoId: photo.id, coverUrl: photo.picUrl })
      .then(album => {
        this.setState(state => ({ albums: state.albums.map(item => (item.id === album.id ? album : item)) }));
        this.showToast('Album cover updated');
      })
      .catch(() => this.showToast('Album cover could not be updated.'));
  };

  saveCaption = () => {
    const { captionEditPhotoId, captionValue, captionDescription, captionAccessPreset } = this.state;
    const data = {
      name: captionValue.trim(),
      description: captionDescription.trim(),
      accessPreset: captionAccessPreset
    };

    changeSchoolPhotoInfo(this.props.user, this.getAlbumId(), captionEditPhotoId, data)
      .then(updatedPhoto => {
        this.setState(state => ({
          photos: state.photos.map(item => (item.id === captionEditPhotoId ? updatedPhoto : item)),
          captionEditPhotoId: '',
          captionValue: '',
          captionDescription: ''
        }));
        this.showToast('Caption updated');
      })
      .catch(() => this.showToast('Caption could not be updated. Please try again.'));
  };

  movePhoto = (fromId: string, toId: string) => {
    if (!fromId || !toId || fromId === toId) return;

    this.setState(state => {
      const nextPhotos = [...state.photos];
      const fromIndex = nextPhotos.findIndex(photo => photo.id === fromId);
      const toIndex = nextPhotos.findIndex(photo => photo.id === toId);

      if (fromIndex < 0 || toIndex < 0) return null;

      const [moved] = nextPhotos.splice(fromIndex, 1);
      nextPhotos.splice(toIndex, 0, moved);

      reorderSchoolGalleryPhotos(
        this.props.user,
        this.getAlbumId(),
        nextPhotos.map(photo => photo.id)
      )
        .then(photos => {
          this.setState({ photos, photosCount: photos.length });
          this.showToast('Gallery order updated');
        })
        .catch(() => {
          this.showToast('Gallery order could not be updated. Please try again.');
          this.load();
        });

      return { photos: nextPhotos, dragPhotoId: '', overPhotoId: '' } as any;
    });
  };

  renderPageHeader() {
    const publishedCount = this.state.photos.filter(photo => this.isPhotoVisible(photo)).length;
    const hiddenCount = this.state.photosCount - publishedCount;
    const selectedAlbum = this.state.albums.find(album => album.id === this.getAlbumId());
    const defaultAlbumId = this.getDefaultAlbumId();
    const regularAlbums = this.state.albums.filter(album => album.id !== defaultAlbumId);

    return (
      <header className="schoolGalleryPageHeader">
        <div className="schoolGalleryPageHeader__title">
          <h1>Photos</h1>
          <span>
            {regularAlbums.length} album{regularAlbums.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="schoolGalleryAlbumPicker">
          <button
            type="button"
            className={`schoolGalleryHomepagePicker${this.isHomepageGallery() ? ' is-active' : ''}`}
            onClick={() => this.selectAlbum(defaultAlbumId)}
          >
            {this.icon('M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z')}
            <span>
              <strong>Homepage gallery</strong>
              <small>Hero slideshow · 3:1 crop · ordered separately</small>
            </span>
          </button>
          <span className="schoolGalleryAlbumPicker__divider">Albums</span>
          <select
            value={this.isHomepageGallery() ? '' : this.getAlbumId()}
            onChange={event => event.target.value && this.selectAlbum(event.target.value)}
            aria-label="Select album"
          >
            <option value="" disabled>
              Select an album
            </option>
            {regularAlbums.map(album => (
              <option value={album.id} key={album.id}>
                {album.name || (album.id === defaultAlbumId ? this.getGalleryTitle() : 'Untitled album')}
                {album.academicYear ? ` · ${album.academicYear}` : ''}
                {album.includeOnBigscreen ? ' · Big screen' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="schoolGalleryButton schoolGalleryButton--secondary"
            onClick={() => this.openAlbumEditor()}
          >
            {this.icon('M12 5v14M5 12h14')} New album
          </button>
          {selectedAlbum && selectedAlbum.id !== defaultAlbumId && (
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--ghost"
              onClick={() => this.openAlbumEditor(selectedAlbum)}
            >
              Edit album
            </button>
          )}
          {selectedAlbum && selectedAlbum.id !== defaultAlbumId && (
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--danger"
              onClick={this.removeCurrentAlbum}
            >
              Delete album
            </button>
          )}
        </div>
        <nav className="schoolGalleryTabs" aria-label="School gallery views">
          <button
            type="button"
            className={this.state.segment === 'all' ? 'is-active' : ''}
            onClick={() => this.setSegment('all')}
          >
            All photos <span>{this.state.photosCount}</span>
          </button>
          <button
            type="button"
            className={this.state.segment === 'published' ? 'is-active' : ''}
            onClick={() => this.setSegment('published')}
          >
            Published <span>{publishedCount}</span>
          </button>
          <button
            type="button"
            className={this.state.segment === 'hidden' ? 'is-active' : ''}
            onClick={() => this.setSegment('hidden')}
          >
            Hidden <span>{hiddenCount}</span>
          </button>
        </nav>
        <button
          type="button"
          className="schoolGalleryButton schoolGalleryButton--primary"
          onClick={() => this.setState({ isUploadOpen: true })}
        >
          {this.icon('M12 5v14M5 12h14')}
          Upload photos
        </button>
      </header>
    );
  }

  renderListToolbar() {
    const canStartReordering = !this.isFilteredView() && this.state.photos.length > 1;
    return (
      <div className="schoolGalleryListToolbar">
        <label className="schoolGallerySearch">
          {this.icon('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3')}
          <input
            value={this.state.searchDraft}
            onChange={event => this.setState({ searchDraft: event.target.value })}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                this.applySearch();
              }
            }}
            placeholder="Search by photo caption"
          />
          {this.state.searchDraft ? (
            <button type="button" onClick={() => this.setSearch('')} aria-label="Clear search">
              ×
            </button>
          ) : null}
        </label>
        <div className="schoolGalleryOrderHint">
          {this.icon('M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01')}
          {this.isFilteredView()
            ? 'Clear filters to reorder photos'
            : this.state.isReordering
            ? 'Drag photos or use the arrow buttons'
            : 'Photos appear publicly in this order'}
        </div>
        <button
          type="button"
          className={`schoolGalleryButton schoolGalleryButton--secondary${this.state.isReordering ? ' is-active' : ''}`}
          disabled={!canStartReordering}
          onClick={() => this.setState(state => ({ isReordering: !state.isReordering, dragPhotoId: '', overPhotoId: '' }))}
        >
          {this.icon('M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01')}
          {this.state.isReordering ? 'Done reordering' : 'Reorder photos'}
        </button>
      </div>
    );
  }

  renderActiveCriteria() {
    const search = this.state.search.trim();
    if (!search) return null;

    return (
      <div className="schoolGalleryActiveFilters">
        <span>
          <b>Search:</b> {search}
          <button type="button" onClick={() => this.setSearch('')} aria-label="Remove search filter">
            ×
          </button>
        </span>
        <button type="button" onClick={this.clearCriteria}>
          Clear all
        </button>
      </div>
    );
  }

  renderPhotoCard(photo: Photo) {
    const visible = this.isPhotoVisible(photo);
    const isDrag = this.state.dragPhotoId === photo.id;
    const isOver = this.state.overPhotoId === photo.id && !isDrag;
    const canReorder = !this.isFilteredView() && this.state.isReordering;

    return (
      <div
        key={photo.id}
        className={`schoolGalleryCard${visible ? '' : ' schoolGalleryCard--hidden'}${canReorder ? ' schoolGalleryCard--reordering' : ''}${
          isDrag ? ' schoolGalleryCard--drag' : ''
        }${isOver ? ' schoolGalleryCard--over' : ''}`}
        draggable={canReorder}
        onDragStart={event => {
          if (!canReorder) return;
          event.dataTransfer.effectAllowed = 'move';
          this.setState({ dragPhotoId: photo.id });
        }}
        onDragEnter={event => {
          if (!canReorder) return;
          event.preventDefault();
          this.setState({ overPhotoId: photo.id });
        }}
        onDragOver={event => {
          if (canReorder) event.preventDefault();
        }}
        onDrop={event => {
          if (!canReorder) return;
          event.preventDefault();
          this.movePhoto(this.state.dragPhotoId, photo.id);
        }}
        onDragEnd={() => this.setState({ dragPhotoId: '', overPhotoId: '' })}
      >
        <button type="button" className="schoolGalleryCard__image" onClick={() => this.onPhotoClick(photo.id)}>
          <img src={photo.picUrl} alt={this.getCaption(photo)} />
        </button>

        <div className="schoolGalleryCard__top">
          {canReorder ? (
            <span title="Drag to reorder">{this.icon('M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01')}</span>
          ) : null}
          {!visible && (
            <b>
              {this.icon(
                'M17.9 17.9A10.4 10.4 0 0 1 12 19c-6.5 0-10-7-10-7a18 18 0 0 1 5.1-6M9.9 4.2A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-2.2 3.2M1 1l22 22M9.9 9.9a3 3 0 0 0 4.2 4.2'
              )}{' '}
              Hidden
            </b>
          )}
        </div>

        <div className="schoolGalleryCard__actions">
          {canReorder ? (
            <>
              <button
                type="button"
                title="Move earlier"
                aria-label={`Move ${this.getCaption(photo)} earlier`}
                disabled={this.state.photos[0].id === photo.id}
                onClick={() => this.movePhotoBy(photo.id, -1)}
              >
                {this.icon('M15 18l-6-6 6-6')}
              </button>
              <button
                type="button"
                title="Move later"
                aria-label={`Move ${this.getCaption(photo)} later`}
                disabled={this.state.photos[this.state.photos.length - 1].id === photo.id}
                onClick={() => this.movePhotoBy(photo.id, 1)}
              >
                {this.icon('M9 18l6-6-6-6')}
              </button>
            </>
          ) : null}
          <button type="button" title={visible ? 'Hide' : 'Publish'} onClick={() => this.toggleVisibility(photo)}>
            {this.icon(
              visible
                ? 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6'
                : 'M17.9 17.9A10.4 10.4 0 0 1 12 19c-6.5 0-10-7-10-7a18 18 0 0 1 5.1-6M9.9 4.2A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-2.2 3.2M1 1l22 22M9.9 9.9a3 3 0 0 0 4.2 4.2'
            )}
          </button>
          <button type="button" title="Edit caption" onClick={() => this.openCaptionEdit(photo)}>
            {this.icon('M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z')}
          </button>
          <button type="button" title="Use as album cover" onClick={() => this.setAlbumCover(photo)}>
            {this.icon('M4 5h16v14H4zM8 15l3-3 2 2 3-4 4 5')}
          </button>
          <button type="button" title="Delete" className="is-danger" onClick={() => this.onDeletePhotoClick(photo.id)}>
            {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}
          </button>
        </div>

        <div className="schoolGalleryCard__caption">
          <div>{this.getCaption(photo)}</div>
          <p>
            {photo.uploadedAt
              ? new Date(photo.uploadedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })
              : ''}
            {photo.uploadedBy && (photo.uploadedBy.name || photo.uploadedBy.role)
              ? ` · ${photo.uploadedBy.name || photo.uploadedBy.role}`
              : ''}
          </p>
        </div>
      </div>
    );
  }

  renderPhotos() {
    const photos = this.getVisiblePhotos();

    if (this.state.photos.length === 0) {
      return (
        <div className="schoolGalleryEmpty">
          <div>
            {this.icon(
              'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M21 15l-5-5L5 21'
            )}
          </div>
          <h2>No photos yet</h2>
          <p>Upload {this.getOwnerLabel()} photos and choose which ones are published in the gallery.</p>
          <button
            type="button"
            className="schoolGalleryButton schoolGalleryButton--primary"
            onClick={() => this.setState({ isUploadOpen: true })}
          >
            {this.icon('M12 5v14M5 12h14')}
            Upload photos
          </button>
        </div>
      );
    }

    if (photos.length === 0) {
      return (
        <div className="schoolGalleryEmpty schoolGalleryEmpty--filtered">
          <div>{this.icon('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3')}</div>
          <h2>No photos match</h2>
          <p>Try another caption or clear the current gallery filters.</p>
          <button
            type="button"
            className="schoolGalleryButton schoolGalleryButton--secondary"
            onClick={this.clearCriteria}
          >
            Clear filters
          </button>
        </div>
      );
    }

    return <div className="schoolGalleryGrid">{photos.map(photo => this.renderPhotoCard(photo))}</div>;
  }

  renderLightbox() {
    const { fullScreenPhotoId, photos } = this.state;
    if (!fullScreenPhotoId) return null;

    const index = this.getFullScreenPhotoIndex();
    const photo = photos[index];
    if (!photo) return null;

    return (
      <div className="schoolGalleryLightbox" onClick={this.closeLightbox}>
        <button type="button" className="schoolGalleryLightbox__close" onClick={this.closeLightbox}>
          {this.icon('M18 6 6 18M6 6l12 12')}
        </button>
        {photos.length > 1 && (
          <button
            type="button"
            className="schoolGalleryLightbox__nav schoolGalleryLightbox__nav--prev"
            onClick={this.onPrevPhotoClick}
          >
            {this.icon('M15 18l-6-6 6-6')}
          </button>
        )}
        {photos.length > 1 && (
          <button
            type="button"
            className="schoolGalleryLightbox__nav schoolGalleryLightbox__nav--next"
            onClick={this.onNextPhotoClick}
          >
            {this.icon('M9 18l6-6-6-6')}
          </button>
        )}
        <div className="schoolGalleryLightbox__image" onClick={event => event.stopPropagation()}>
          <img src={photo.picUrl} alt={this.getCaption(photo)} />
        </div>
        <div className="schoolGalleryLightbox__meta" onClick={event => event.stopPropagation()}>
          <div>{this.getCaption(photo)}</div>
          <p>
            {index + 1} of {photos.length}
          </p>
          <div>
            <button type="button" onClick={() => this.toggleVisibility(photo)}>
              {this.icon(
                this.isPhotoVisible(photo)
                  ? 'M17.9 17.9A10.4 10.4 0 0 1 12 19c-6.5 0-10-7-10-7a18 18 0 0 1 5.1-6M9.9 4.2A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-2.2 3.2M1 1l22 22M9.9 9.9a3 3 0 0 0 4.2 4.2'
                  : 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6'
              )}
              {this.isPhotoVisible(photo) ? 'Hide' : 'Publish'}
            </button>
            <button type="button" className="is-danger" onClick={() => this.onDeletePhotoClick(photo.id)}>
              {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderUploadModal() {
    if (!this.state.isUploadOpen) return null;

    return (
      <div className="schoolGalleryModalOverlay">
        <div className="schoolGalleryUploadModal" role="dialog" aria-modal="true" aria-label="Upload photos">
          <div className="schoolGalleryUploadModal__header">
            <div>
              {this.icon(
                'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M21 15l-5-5L5 21'
              )}
              <div>
                <h2>Upload photos</h2>
                <p>
                  {this.isHomepageGallery()
                    ? 'Add landscape images, set a 3:1 hero crop and arrange their display order.'
                    : 'Add portrait or landscape images. Cropping is optional.'}
                </p>
              </div>
            </div>
            <button type="button" onClick={this.closeUpload} aria-label="Close upload">
              {this.icon('M18 6 6 18M6 6l12 12')}
            </button>
          </div>

          <div className="schoolGalleryUploadModal__body">
            <label
              className="schoolGalleryDropzone"
              onDragOver={event => event.preventDefault()}
              onDrop={event => {
                event.preventDefault();
                this.onFilesSelected(event.dataTransfer.files);
              }}
            >
              {this.icon('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12')}
              <b>Drag photos here</b>
              <span>or browse from your device - JPG or PNG, multiple at once.</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={event => this.onFilesSelected(event.target.files)}
              />
            </label>

            {this.state.uploadQueue.length > 0 && (
              <div className="schoolGalleryUploadQueue">
                {this.state.uploadQueue.map(item => (
                  <div key={item.id} className="schoolGalleryUploadItem">
                    <img src={item.previewUrl} alt="Preview" />
                    <button
                      type="button"
                      className="schoolGalleryUploadItem__crop"
                      onClick={() => this.openCrop(item.id)}
                    >
                      {this.icon('M6 2v14a2 2 0 0 0 2 2h14M2 6h14a2 2 0 0 1 2 2v14')}
                      {item.isCropped ? 'Adjust crop' : this.isHomepageGallery() ? 'Crop 3:1' : 'Crop (optional)'}
                    </button>
                    <input
                      value={item.caption}
                      onChange={event => this.setUploadCaption(item.id, event.target.value)}
                      placeholder="Caption"
                    />
                    <button
                      type="button"
                      className="schoolGalleryUploadItem__remove"
                      onClick={() => this.removeUploadItem(item.id)}
                    >
                      {this.icon('M18 6 6 18M6 6l12 12')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="schoolGalleryUploadModal__footer">
            <button type="button" className="schoolGalleryButton schoolGalleryButton--ghost" onClick={this.closeUpload}>
              Cancel
            </button>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--primary"
              disabled={this.state.isUploading || this.state.uploadQueue.length === 0}
              onClick={this.onSubmitUpload}
            >
              {this.icon('M20 6 9 17l-5-5')}
              {this.state.isUploading ? 'Uploading...' : `Add ${this.state.uploadQueue.length} to gallery`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderCropModal() {
    const item = this.state.uploadQueue.find(queueItem => queueItem.id === this.state.cropUploadItemId);
    if (!item) return null;

    return (
      <div className="schoolGalleryCropOverlay">
        <div className="schoolGalleryCropModal">
          <div className="schoolGalleryCropModal__header">
            <div>
              <h2>Crop for public gallery</h2>
              <p>Choose the wide 3:1 banner area used on the school website.</p>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ cropUploadItemId: '', completedCrop: null })}
              aria-label="Close crop"
            >
              {this.icon('M18 6 6 18M6 6l12 12')}
            </button>
          </div>
          <div className="schoolGalleryCropModal__body">
            <ReactCrop
              src={item.previewUrl}
              crop={this.state.crop}
              keepSelection
              imageAlt="Crop preview"
              onImageLoaded={this.onCropImageLoad}
              onChange={(_, percentCrop) => this.setState({ crop: percentCrop as PercentCrop })}
              onComplete={pixelCrop => this.setState({ completedCrop: pixelCrop as PixelCrop })}
            />
          </div>
          <div className="schoolGalleryCropModal__footer">
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--ghost"
              onClick={() => this.setState({ cropUploadItemId: '', completedCrop: null })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--primary"
              disabled={!this.state.completedCrop}
              onClick={this.applyCrop}
            >
              Apply crop
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderDeleteConfirmation() {
    const photo = this.state.photos.find(item => item.id === this.state.deletePhotoId);
    if (!photo) return null;

    return (
      <div className="schoolGalleryModalOverlay schoolGalleryModalOverlay--confirm">
        <div className="schoolGalleryDeleteModal" role="dialog" aria-modal="true" aria-label="Delete photo">
          <div className="schoolGalleryDeleteModal__icon">!</div>
          <div>
            <h2>Delete photo?</h2>
            <p>
              “{this.getCaption(photo)}” will be permanently removed from the {this.getOwnerLabel()} gallery.
            </p>
          </div>
          <div className="schoolGalleryDeleteModal__actions">
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--ghost"
              onClick={() => this.setState({ deletePhotoId: '' })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--danger"
              onClick={this.confirmDeletePhoto}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderCaptionModal() {
    if (!this.state.captionEditPhotoId) return null;

    return (
      <div className="schoolGalleryModalOverlay">
        <div className="schoolGalleryCaptionModal" role="dialog" aria-modal="true" aria-label="Edit caption">
          <header>
            <h2>Edit caption</h2>
            <button
              type="button"
              onClick={() => this.setState({ captionEditPhotoId: '', captionValue: '' })}
              aria-label="Close caption editor"
            >
              {this.icon('M18 6 6 18M6 6l12 12')}
            </button>
          </header>
          <div className="schoolGalleryCaptionModal__body">
            <label>
              Caption
              <input
                value={this.state.captionValue}
                autoFocus
                onChange={event => this.setState({ captionValue: event.target.value })}
                onKeyDown={event => {
                  if (event.key === 'Enter') this.saveCaption();
                }}
                placeholder="Add a short photo caption"
              />
            </label>
            <label>
              Description
              <textarea
                value={this.state.captionDescription}
                onChange={event => this.setState({ captionDescription: event.target.value })}
                placeholder="Add context for this photo"
              />
            </label>
            <label>
              Visibility
              <select
                value={this.state.captionAccessPreset}
                onChange={event => this.setState({ captionAccessPreset: event.target.value as Photo['accessPreset'] })}
              >
                <option value={ACCESS_PRESET.PUBLIC}>Public</option>
                <option value={ACCESS_PRESET.ALL_PARTICIPANT_PARENTS}>Your school only</option>
                <option value={ACCESS_PRESET.PRIVATE}>Private</option>
              </select>
            </label>
          </div>
          <footer>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--secondary"
              onClick={() => this.setState({ captionEditPhotoId: '', captionValue: '' })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--primary"
              onClick={this.saveCaption}
            >
              {this.icon('M20 6 9 17l-5-5')}Save
            </button>
          </footer>
        </div>
      </div>
    );
  }

  renderAlbumEditor() {
    if (!this.state.isAlbumEditorOpen) return null;
    const draft = this.state.albumDraft;
    const setDraft = patch => this.setState({ albumDraft: { ...this.state.albumDraft, ...patch } });

    return (
      <div className="schoolGalleryModalOverlay">
        <div
          className="schoolGalleryCaptionModal schoolGalleryAlbumModal"
          role="dialog"
          aria-modal="true"
          aria-label="Album details"
        >
          <header>
            <h2>{this.state.editingAlbumId ? 'Edit album' : 'Create album'}</h2>
            <button
              type="button"
              onClick={() => this.setState({ isAlbumEditorOpen: false })}
              aria-label="Close album editor"
            >
              {this.icon('M18 6 6 18M6 6l12 12')}
            </button>
          </header>
          <div className="schoolGalleryCaptionModal__body schoolGalleryAlbumForm">
            <label>
              Album name
              <input autoFocus value={draft.name} onChange={event => setDraft({ name: event.target.value })} />
            </label>
            <label>
              Description
              <textarea value={draft.description} onChange={event => setDraft({ description: event.target.value })} />
            </label>
            <label>
              Academic year
              <input
                value={draft.academicYear}
                placeholder="2026/27"
                onChange={event => setDraft({ academicYear: event.target.value })}
              />
            </label>
            <label>
              Category
              <select
                value={draft.category}
                onChange={event => setDraft({ category: event.target.value as SCHOOL_ALBUM_CATEGORY })}
              >
                <option value="SPORT_TEAM">Sport &amp; teams</option>
                <option value="EVENT_ACTION">Events &amp; action</option>
                <option value="SCHOOL_LIFE">School life</option>
                <option value="GENERAL">General</option>
              </select>
            </label>
            <label>
              Visibility
              <select
                value={draft.accessPreset}
                onChange={event => setDraft({ accessPreset: event.target.value as 'PUBLIC' | 'PRIVATE', ...(event.target.value === 'PRIVATE' ? { includeOnBigscreen: false } : {}) })}
              >
                <option value="PUBLIC">Public</option>
                <option value="PRIVATE">Private</option>
              </select>
            </label>
            {!this.isHomepageGallery(this.state.editingAlbumId) && <label className={`schoolGalleryBigscreenOption ${draft.accessPreset !== 'PUBLIC' ? 'is-disabled' : ''}`}>
              <span><strong>Include on school big screen</strong><small>Public photos from this album may appear in “Moments from this year”.</small></span>
              <input type="checkbox" checked={draft.includeOnBigscreen} disabled={draft.accessPreset !== 'PUBLIC'} onChange={event => setDraft({ includeOnBigscreen: event.target.checked })}/>
            </label>}
          </div>
          <footer>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--secondary"
              onClick={() => this.setState({ isAlbumEditorOpen: false })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolGalleryButton schoolGalleryButton--primary"
              disabled={!draft.name.trim()}
              onClick={this.saveAlbum}
            >
              Save album
            </button>
          </footer>
        </div>
      </div>
    );
  }

  renderToast() {
    if (!this.state.toast) return null;
    const isError = this.state.toast.indexOf('could not') !== -1;
    return (
      <div className={isError ? 'schoolGalleryToast schoolGalleryToast--error' : 'schoolGalleryToast'}>
        {this.icon(isError ? 'M12 8v5M12 17h.01' : 'M20 6 9 17l-5-5')}
        {this.state.toast}
      </div>
    );
  }

  render() {
    if (this.state.isLoading) return <Loader />;

    if (this.state.loadError) {
      return (
        <div className="schoolGalleryPage">
          {this.renderPageHeader()}
          <div className="schoolGalleryShell">
            <div className="schoolGalleryLoadError" role="alert">
              <div>
                {this.icon(
                  'M12 9v4M12 17h.01M10.3 3.6 2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0z'
                )}
              </div>
              <h2>Could not load {this.getGalleryTitle().toLowerCase()}</h2>
              <p>{this.state.loadError}</p>
              <button type="button" className="schoolGalleryButton schoolGalleryButton--primary" onClick={this.load}>
                {this.icon('M20 11a8.1 8.1 0 1 1-2.4-5.7L20 7M20 2v5h-5')}
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`schoolGalleryPage ${
          this.isHomepageGallery() ? 'schoolGalleryPage--homepage' : 'schoolGalleryPage--album'
        }`}
      >
        {this.renderPageHeader()}
        <div className="schoolGalleryShell">
          <section className="schoolGalleryListCard">
            {this.renderListToolbar()}
            {this.renderActiveCriteria()}
            {this.renderPhotos()}
          </section>
        </div>
        {this.renderLightbox()}
        {this.renderUploadModal()}
        {this.renderCropModal()}
        {this.renderCaptionModal()}
        {this.renderAlbumEditor()}
        {this.renderDeleteConfirmation()}
        {this.renderToast()}
      </div>
    );
  }
}
